import { useEstimateGate } from "@features/billing/components/CreditEstimate";
import SendIcon from "@mui/icons-material/SendRounded";
import StopIcon from "@mui/icons-material/StopRounded";
import {
  Box,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useChatStore } from "@stores/chatStore";
import {
  type KeyboardEvent,
  type Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export interface ChatInputHandle {
  focus: () => void;
}

interface ChatInputProps {
  onSend: (message: string) => void;
  onStop: () => void;
  disabled?: boolean;
  isStreaming?: boolean;
  placeholder?: string;
  ref?: Ref<ChatInputHandle>;
}

export function ChatInput({
  onSend,
  onStop,
  disabled = false,
  isStreaming = false,
  placeholder = "Ask about your circuit…",
  ref,
}: ChatInputProps) {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Model picker state lives on the chat store so the chosen model
  // survives re-mounts (project switches, route changes) within a
  // single conversation. The picker locks once the conversation has
  // any messages — the backend pins the model on the ChatSession at
  // first turn and ignores subsequent overrides regardless, so the
  // UI mirrors that contract instead of pretending the choice is
  // still mutable.
  const selectedModel = useChatStore((s) => s.selectedModel);
  const setSelectedModel = useChatStore((s) => s.setSelectedModel);
  const messageCount = useChatStore((s) => s.messages.length);
  const modelLocked = messageCount > 0;

  // External prefill — features outside the chat (the DRC
  // AskAgentToFixButton, future BOM substitute gestures) push text
  // into the composer via `chatStore.prefillComposer`. The seq
  // counter forces the effect to re-fire even when the same text
  // is queued twice.
  const composerDraft = useChatStore((s) => s.composerDraft);
  const composerDraftSeq = useChatStore((s) => s.composerDraftSeq);
  const clearComposerDraft = useChatStore((s) => s.clearComposerDraft);
  // biome-ignore lint/correctness/useExhaustiveDependencies: composerDraftSeq is the change signal; composerDraft / clearComposerDraft are accessed via closure.
  useEffect(() => {
    if (composerDraftSeq > 0 && composerDraft) {
      setValue(composerDraft);
      clearComposerDraft();
      // Defer focus until after the value lands so the cursor sits
      // at the end of the prefilled text.
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el) {
          el.focus();
          const len = composerDraft.length;
          try {
            el.setSelectionRange(len, len);
          } catch {
            /* setSelectionRange unsupported on some control types */
          }
        }
      });
    }
  }, [composerDraftSeq]);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
  }));

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue("");
  };

  const modelLockTooltip = modelLocked
    ? "Model is locked once the conversation starts. Start a new chat to switch."
    : "";

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Pre-flight credit check. Server still does the real gating; this
  // is purely a friendlier UX so the user doesn't fire a doomed turn.
  const { hasFunds } = useEstimateGate(value.length, selectedModel);

  // "Armed" — the user has typed something and we're not currently
  // streaming. Drives the send-button's amber glow (the brand's
  // pin-1 ember signal: lit chip = ready to fire).
  const armed =
    !isStreaming && !disabled && value.trim().length > 0 && hasFunds;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        bgcolor: "surface.raised",
        border: "1px solid",
        borderColor: "border.default",
        borderRadius: 2.5,
        px: 1.5,
        py: 1,
        transition: (t) =>
          `border-color ${t.motion.fast}, box-shadow ${t.motion.fast}`,
        "&:focus-within": {
          borderColor: "primary.main",
          boxShadow: (t) => `0 0 0 3px ${alpha(t.palette.primary.main, 0.18)}`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Tooltip title={modelLockTooltip} placement="top">
          <ToggleButtonGroup
            value={selectedModel}
            exclusive
            size="small"
            disabled={modelLocked || disabled}
            onChange={(_, next) => {
              if (next === "sonnet" || next === "opus") {
                setSelectedModel(next);
              }
            }}
            aria-label="Claude model"
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontSize: "0.7rem",
                lineHeight: 1.2,
                py: 0.25,
                px: 1,
                color: "text.secondary",
                border: "1px solid",
                borderColor: "border.default",
                "&.Mui-selected": {
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.16),
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.22),
                  },
                },
              },
            }}
          >
            <ToggleButton value="sonnet" aria-label="Use Claude Sonnet">
              Sonnet
            </ToggleButton>
            <ToggleButton value="opus" aria-label="Use Claude Opus">
              Opus
            </ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
        <TextField
          fullWidth
          multiline
          maxRows={6}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          variant="standard"
          inputRef={inputRef}
          slotProps={{
            input: { disableUnderline: true },
          }}
          sx={{
            "& .MuiInputBase-root": {
              background: "transparent",
              color: "text.primary",
              fontSize: "0.9375rem",
              lineHeight: 1.55,
              // Pin-1 amber caret — typing is the moment the chip is
              // about to be lit. Hooks the brand's "live signal" cue
              // to the most-tactile interaction in the product.
              caretColor: (t) => t.palette.warning.main,
            },
            "& .MuiInputBase-input::placeholder": {
              color: "text.disabled",
              opacity: 1,
            },
          }}
        />

        <Tooltip title={isStreaming ? "Stop response (Esc)" : "Send (Enter)"}>
          <span>
            <IconButton
              size="small"
              onClick={isStreaming ? onStop : handleSend}
              disabled={
                !isStreaming && (disabled || !value.trim() || !hasFunds)
              }
              aria-label={
                isStreaming
                  ? "Stop response"
                  : !hasFunds
                    ? "Send message (insufficient credits — top up to continue)"
                    : "Send message"
              }
              sx={{
                // "Armed" state: when the user has typed something we're
                // about to send, the button lights amber — same brand
                // signal as the streaming caret. Inert/streaming/empty
                // stay on the cool palette so the amber means "ready
                // to fire" specifically, not just "an interactive
                // control exists here".
                color: isStreaming
                  ? "text.secondary"
                  : armed
                    ? "warning.main"
                    : "text.disabled",
                flexShrink: 0,
                transition: (t) =>
                  `color ${t.motion.fast}, box-shadow ${t.motion.fast}`,
                boxShadow: armed
                  ? (t) => `0 0 0 2px ${alpha(t.palette.warning.main, 0.16)}`
                  : "none",
                "&:hover": {
                  color: isStreaming
                    ? "text.primary"
                    : armed
                      ? "warning.light"
                      : "primary.light",
                  bgcolor: (t) =>
                    alpha(
                      armed && !isStreaming
                        ? t.palette.warning.main
                        : t.palette.primary.main,
                      0.12,
                    ),
                },
              }}
            >
              {isStreaming ? (
                <StopIcon fontSize="small" />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}
